import api from './api';
import type {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  Project,
  CreateProjectRequest,
  Task,
  CreateTaskRequest,
  Issue,
  CreateIssueRequest,
  UpdateIssueRequest,
  DashboardStats,
  ProjectProgress,
  RecentActivity,
  User,
  Report,
  ReportTemplate,
  Message,
  Notification,
  Todo,
  Resource,
  TaskActivity,
  MySubtask,
} from './types';

// 认证服务
export const authApi = {
  // 登录
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', data);
    // 后端直接返回 {access_token, token_type, user}
    return {
      ...response.data,
      token: response.data.access_token,
    } as AuthResponse;
  },

  // 注册
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', {
      username: data.username,
      email: data.email,
      password: data.password,
      // confirmPassword 只用于前端验证，不发送到后端
    });
    // 后端直接返回 {access_token, token_type, user}
    return {
      ...response.data,
      token: response.data.access_token,
    } as AuthResponse;
  },

  // 验证 token
  verifyToken: async (): Promise<User> => {
    const response = await api.get<any>('/auth/me');
    const userData = response.data;
    // 兼容后端的 username/full_name 和前端的 name
    return {
      id: String(userData.id),
      name: userData.name || userData.full_name || userData.username || userData.email,
      email: userData.email,
      role: userData.role || 'member',
      avatar: userData.avatar,
      department: userData.department,
    } as User;
  },

  // 登出
  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
};

// 项目服务
export const projectApi = {
  // 获取项目列表
  getProjects: async (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
    priority?: string;
    search?: string;
  }): Promise<Project[]> => {
    const response = await api.get('/projects', { params });
    console.log('[projectApi.getProjects] response:', response.data);
    // 处理可能的响应格式
    const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    console.log('[projectApi.getProjects] parsed data:', data);
    return data as Project[];
  },

  // 获取项目详情
  getProject: async (id: string): Promise<Project> => {
    const response = await api.get(`/projects/${id}`);
    return response.data as Project;
  },

  // 创建项目
  createProject: async (data: CreateProjectRequest): Promise<Project> => {
    // 将前端格式转换为后端格式
    const backendData: any = {
      name: data.name,
      description: data.description,
      status: data.status,
      priority: data.priority,
      owner_id: data.managerId ? (typeof data.managerId === 'string' ? parseInt(data.managerId, 10) : data.managerId) : undefined,
      startDate: data.startDate,
      endDate: data.endDate,
    };
    const response = await api.post('/projects', backendData);
    return response.data as Project;
  },

  // 更新项目
  updateProject: async (id: string, data: Partial<CreateProjectRequest>): Promise<Project> => {
    // 将前端格式转换为后端格式
    const backendData: any = {};
    if (data.name !== undefined) backendData.name = data.name;
    if (data.description !== undefined) backendData.description = data.description;
    if (data.status !== undefined) backendData.status = data.status;
    if (data.priority !== undefined) backendData.priority = data.priority;
    if (data.managerId !== undefined) backendData.owner_id = typeof data.managerId === 'string' ? parseInt(data.managerId, 10) : data.managerId;
    if (data.startDate !== undefined) backendData.startDate = data.startDate;
    if (data.endDate !== undefined) backendData.endDate = data.endDate;
    if (data.progress !== undefined) backendData.progress = data.progress;

    const response = await api.put(`/projects/${id}`, backendData);
    return response.data as Project;
  },

  // 删除项目
  deleteProject: async (id: string): Promise<void> => {
    await api.delete(`/projects/${id}`);
  },

  // 导出项目到 Excel
  exportProjectToExcel: async (projectId: string): Promise<Blob> => {
    const response = await api.get(`/projects/${projectId}/export`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // 导出 Excel 模板
  exportTemplate: async (): Promise<Blob> => {
    const response = await api.get('/template/project', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // 从 Excel 导入项目
  importProjectFromExcel: async (
    formData: FormData,
    projectId: string
  ): Promise<{ success: boolean; data?: { tasks_count: number }; errors?: string[] }> => {
    const response = await api.post(`/projects/${projectId}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// 任务服务
export const taskApi = {
  // 获取任务列表（树状结构）
  getTasks: async (params?: {
    page?: number;
    pageSize?: number;
    projectId?: string;
    parentId?: string;
    status?: string;
    priority?: string;
    assigneeId?: string;
  }): Promise<Task[]> => {
    // 将 camelCase 参数转换为 snake_case
    const snakeParams: Record<string, any> = {};
    if (params?.page !== undefined) snakeParams.page = params.page;
    if (params?.pageSize !== undefined) snakeParams.limit = params.pageSize;
    if (params?.projectId !== undefined) snakeParams.project_id = params.projectId;
    if (params?.parentId !== undefined) snakeParams.parent_id = params.parentId;
    if (params?.status !== undefined) snakeParams.status = params.status;
    if (params?.priority !== undefined) snakeParams.priority = params.priority;
    if (params?.assigneeId !== undefined) snakeParams.assignee_id = params.assigneeId;

    const response = await api.get('/tasks/tree', { params: snakeParams });
    console.log('[taskApi.getTasks] response:', response.data);
    // 将后端返回的数据转换为前端需要的格式（递归处理 children）
    const transformTask = (task: any): Task => ({
      ...task,
      parentId: task.parent_id ? String(task.parent_id) : undefined,
      parentTaskId: task.parent_id ? String(task.parent_id) : undefined,
      projectId: task.project_id ? String(task.project_id) : undefined,
      assigneeId: task.assignee_id ? String(task.assignee_id) : undefined,
      executorIds: task.executor_ids?.map((id: number) => String(id)) || [],
      dependencies: task.depends_on || [],  // 后端已经返回 { id, title } 对象数组
      startDate: task.start_date,
      dueDate: task.end_date,
      children: task.children?.map(transformTask) || [],
    });

    const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    console.log('[taskApi.getTasks] parsed data:', data);
    return (data as any[]).map(transformTask) as Task[];
  },

  // 获取任务详情
  getTask: async (id: string): Promise<Task> => {
    const response = await api.get(`/tasks/${id}`);
    const task = response.data as any;
    // 将后端返回的 snake_case 转换为 camelCase
    return {
      ...task,
      parentId: task.parent_id ? String(task.parent_id) : undefined,
      parentTaskId: task.parent_id ? String(task.parent_id) : undefined,
      // 优先使用 project_id，如果为 0 或 undefined 则使用 project.id
      projectId: task.project_id ? String(task.project_id) : (task.project ? String(task.project.id) : undefined),
      assigneeId: task.assignee_id ? String(task.assignee_id) : undefined,
      executorIds: task.executor_ids?.map((id: number) => String(id)) || [],
      executors: task.executors || [],
      // 处理嵌套的 project 对象
      project: task.project ? {
        id: String(task.project.id),
        name: task.project.name
      } : undefined,
      startDate: task.start_date,
      dueDate: task.end_date,
      estimatedHours: task.estimated_hours,
      actualHours: task.actual_hours,
      children: task.children || [],
    } as Task;
  },

  // 创建任务
  createTask: async (data: CreateTaskRequest): Promise<Task> => {
    // 将前端格式转换为后端格式
    const parseId = (id: string | number | undefined | null): number | null => {
      if (id === undefined || id === null || id === '') {
        return null;
      }
      return typeof id === 'string' ? parseInt(id, 10) : id;
    };

    const backendData: any = {
      title: data.title,
      description: data.description,
      priority: data.priority,
      project_id: parseId(data.projectId),
      assignee_id: parseId(data.assigneeId) || null,
      parent_id: parseId(data.parentId) || parseId(data.parentTaskId) || null,
      depends_on: (data.dependencies || []).map((id) => parseId(id)),
      executor_ids: (data.executorIds || []).map((id) => parseId(id)),
      start_date: data.startDate ? data.startDate.split(' ')[0] : null,
      end_date: data.dueDate ? data.dueDate.split(' ')[0] : null,
      estimated_hours: data.estimatedHours,
    };

    // 移除 null 值的字段（可选字段）
    Object.keys(backendData).forEach(key => {
      if (backendData[key] === null) delete backendData[key];
    });

    const response = await api.post('/tasks', backendData);
    return response.data as Task;
  },

  // 更新任务
  updateTask: async (id: string, data: Partial<CreateTaskRequest>): Promise<Task> => {
    // 将前端格式转换为后端格式
    const backendData: any = {};
    if (data.title !== undefined) backendData.title = data.title;
    if (data.description !== undefined) backendData.description = data.description;
    if (data.status !== undefined) backendData.status = data.status;
    if (data.priority !== undefined) backendData.priority = data.priority;
    if (data.assigneeId !== undefined) backendData.assignee_id = data.assigneeId;
    if (data.parentId !== undefined) backendData.parent_id = data.parentId;
    if (data.dependencies !== undefined) backendData.depends_on = data.dependencies;
    if (data.executorIds !== undefined) backendData.executor_ids = data.executorIds;
    if (data.startDate !== undefined) backendData.start_date = data.startDate ? data.startDate.split(' ')[0] : null;
    if (data.dueDate !== undefined) backendData.end_date = data.dueDate ? data.dueDate.split(' ')[0] : null;
    if (data.progress !== undefined) backendData.progress = data.progress;
    if (data.estimatedHours !== undefined) backendData.estimated_hours = data.estimatedHours;
    if (data.actualHours !== undefined) backendData.actual_hours = data.actualHours;

    const response = await api.put(`/tasks/${id}`, backendData);
    return response.data as Task;
  },

  // 更新任务状态（拖拽专用）
  updateTaskStatus: async (id: string, status: string): Promise<Task> => {
    const response = await api.patch(`/tasks/${id}/status`, { status });
    return response.data as Task;
  },

  // 获取任务状态历史
  getTaskStatusHistory: async (id: string): Promise<any[]> => {
    const response = await api.get(`/tasks/${id}/status-history`);
    return response.data || [];
  },

  // 删除任务
  deleteTask: async (id: string): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  },

  // 更新任务进度
  updateTaskProgress: async (id: string, progress: number, remark?: string): Promise<Task> => {
    const response = await api.put(`/tasks/${id}/progress`, { progress, remark });
    return response.data as Task;
  },

  // 获取任务活动记录
  getTaskActivities: async (id: string): Promise<TaskActivity[]> => {
    const response = await api.get(`/tasks/${id}/activities`);
    const activities = Array.isArray(response.data) ? response.data : [];
    // 将后端返回的 snake_case 转换为 camelCase
    return activities.map((a: any) => ({
      ...a,
      taskId: String(a.task_id),
      userId: String(a.user_id),
      userName: a.user_name,
      userAvatar: a.user_avatar,
      oldValue: a.old_value,
      newValue: a.new_value,
      createdAt: a.created_at,
    }));
  },
};

// 问题服务
// 转换后端 snake_case 到前端 camelCase
const convertIssueToFrontend = (issue: any): Issue => ({
  id: String(issue.id),
  title: issue.title,
  description: issue.description,
  status: issue.status,
  priority: issue.priority,
  severity: issue.severity,
  type: issue.type,
  project_id: issue.project_id,
  taskId: issue.task_id ? String(issue.task_id) : undefined,
  task_id: issue.task_id ? String(issue.task_id) : undefined,
  reporter_id: issue.reporter_id,
  assignee_id: issue.assignee_id ? String(issue.assignee_id) : undefined,
  sub_assignee_id: issue.sub_assignee_id ? String(issue.sub_assignee_id) : undefined,
  // 兼容别名
  projectId: issue.project_id ? String(issue.project_id) : undefined,
  reporterId: issue.reporter_id ? String(issue.reporter_id) : undefined,
  assigneeId: issue.assignee_id ? String(issue.assignee_id) : undefined,
  subAssigneeId: issue.sub_assignee_id ? String(issue.sub_assignee_id) : undefined,
  // 关联数据
  reporter: issue.reporter ? { id: String(issue.reporter.id), name: issue.reporter.name || issue.reporter.full_name || issue.reporter.username, avatar: issue.reporter.avatar } : undefined,
  assignee: issue.assignee ? { id: String(issue.assignee.id), name: issue.assignee.name || issue.assignee.full_name || issue.assignee.username, avatar: issue.assignee.avatar } : undefined,
  subAssignee: issue.sub_assignee ? { id: String(issue.sub_assignee.id), name: issue.sub_assignee.name || issue.sub_assignee.full_name || issue.sub_assignee.username, avatar: issue.sub_assignee.avatar } : undefined,
  project: issue.project ? { id: String(issue.project.id), name: issue.project.name } : undefined,
  task: issue.task ? { id: String(issue.task.id), title: issue.task.title } : undefined,
  attachments: issue.attachments,
  createdAt: issue.created_at,
  updatedAt: issue.updated_at,
  resolvedAt: issue.resolved_at,
});

export const issueApi = {
  // 获取问题列表
  getIssues: async (params?: {
    page?: number;
    pageSize?: number;
    projectId?: string;
    status?: string;
    priority?: string;
    type?: string;
    taskId?: string;
  }): Promise<Issue[]> => {
    const response = await api.get('/issues', { params });
    console.log('[issueApi.getIssues] response:', response.data);
    const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    console.log('[issueApi.getIssues] parsed data:', data);
    return data.map((issue: any) => convertIssueToFrontend(issue)) as Issue[];
  },

  // 获取问题详情
  getIssue: async (id: string): Promise<Issue> => {
    const response = await api.get(`/issues/${id}`);
    return convertIssueToFrontend(response.data) as Issue;
  },

  // 创建问题
  createIssue: async (data: CreateIssueRequest): Promise<Issue> => {
    const response = await api.post('/issues', data);
    return convertIssueToFrontend(response.data) as Issue;
  },

  // 更新问题
  updateIssue: async (id: string, data: UpdateIssueRequest): Promise<Issue> => {
    const response = await api.put(`/issues/${id}`, data);
    return convertIssueToFrontend(response.data) as Issue;
  },

  // 删除问题
  deleteIssue: async (id: string): Promise<void> => {
    await api.delete(`/issues/${id}`);
  },

  // 分配问题
  assignIssue: async (id: string, data: { assignee_id: string | number; sub_assignee_id?: string | number }): Promise<Issue> => {
    const response = await api.post(`/issues/${id}/assign`, data);
    return convertIssueToFrontend(response.data) as Issue;
  },

  // 解决问题
  resolveIssue: async (id: string, data?: { resolution: string }): Promise<Issue> => {
    const response = await api.post(`/issues/${id}/resolve`, data);
    return convertIssueToFrontend(response.data) as Issue;
  },

  // 关闭问题
  closeIssue: async (id: string): Promise<Issue> => {
    const response = await api.post(`/issues/${id}/close`);
    return convertIssueToFrontend(response.data) as Issue;
  },

  // 重新打开问题
  reopenIssue: async (id: string): Promise<Issue> => {
    const response = await api.post(`/issues/${id}/reopen`);
    return convertIssueToFrontend(response.data) as Issue;
  },

  // 上传附件
  uploadAttachment: async (id: string, file: File): Promise<{ id: string; file_name: string; file_path: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/issues/${id}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // 删除附件
  deleteAttachment: async (issueId: string, attachmentId: string): Promise<void> => {
    await api.delete(`/issues/${issueId}/attachments/${attachmentId}`);
  },

  // 获取任务相关问题列表
  getTaskIssues: async (taskId: string): Promise<Issue[]> => {
    const response = await api.get(`/tasks/${taskId}/issues`);
    return response.data as Issue[];
  },
};

// 仪表盘服务
export const dashboardApi = {
  // 获取仪表盘统计数据
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/dashboard/stats');
    return response.data as DashboardStats;
  },

  // 获取项目进度
  getProjectProgress: async (): Promise<ProjectProgress[]> => {
    const response = await api.get('/dashboard/project-progress');
    return response.data as ProjectProgress[];
  },

  // 获取最近活动（支持筛选）
  getRecentActivities: async (params?: {
    entity_type?: string;
    action?: string;
    entity_name?: string;
    limit?: number;
  }): Promise<RecentActivity[]> => {
    const response = await api.get('/dashboard/recent-activities', { params });
    return response.data as RecentActivity[];
  },

  // 注意：活动筛选选项已移至前端硬编码，无需 API 调用
};

// 用户服务
export const userApi = {
  // 获取用户列表
  getUsers: async (params?: {
    page?: number;
    pageSize?: number;
    role?: string;
    search?: string;
  }): Promise<User[]> => {
    const response = await api.get('/users', { params });
    // 处理可能的响应格式，映射为前端 User 格式
    const data = Array.isArray(response.data) ? response.data : (response.data.data || []);
    return data.map((user: any) => ({
      id: String(user.id),
      name: user.full_name || user.username || user.name,
      email: user.email,
      role: user.role,
      avatar: user.avatar,
      department: user.department,
      phone: user.phone,
    })) as User[];
  },

  // 获取用户详情
  getUser: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    // 后端直接返回用户对象
    return {
      id: String(response.data.id),
      name: response.data.full_name || response.data.username || response.data.name,
      email: response.data.email,
      role: response.data.role,
      avatar: response.data.avatar,
      department: response.data.department,
    } as User;
  },

  // 更新用户信息
  updateUser: async (id: string, data: Partial<User>): Promise<User> => {
    const response = await api.put(`/users/${id}`, {
      full_name: (data as any).full_name,
      avatar: (data as any).avatar,
      department: (data as any).department,
      phone: (data as any).phone,
    });
    return {
      id: String(response.data.id),
      name: response.data.full_name || response.data.username || response.data.name,
      email: response.data.email,
      role: response.data.role,
      avatar: response.data.avatar,
      department: response.data.department,
      phone: response.data.phone,
    } as User;
  },
};

// 报告服务
export const reportApi = {
  // 获取报告列表
  getReports: async (params?: {
    page?: number;
    pageSize?: number;
    type?: string;
    userId?: string;
    projectId?: string;
  }): Promise<Report[]> => {
    const response = await api.get('/reports', { params });
    return response.data as Report[];
  },

  // 创建报告
  createReport: async (data: {
    title: string;
    type: 'daily' | 'weekly' | 'biweekly' | 'monthly';
    content: any;
    projectId?: string;
    period: {
      start: string;
      end: string;
    };
  }): Promise<Report> => {
    const response = await api.post('/reports', data);
    return response.data as Report;
  },

  // 自动生成报告预览（不保存）
  autoGeneratePreview: async (
    reportType: 'daily' | 'weekly' | 'biweekly' | 'monthly',
    referenceDate?: string,
    projectIds?: number[]
  ): Promise<{ success: boolean; data: any }> => {
    const params: any = { report_type: reportType };
    if (referenceDate) params.reference_date = referenceDate;
    if (projectIds && projectIds.length > 0) params.project_ids = projectIds.join(',');
    const response = await api.get('/reports/auto/generate', { params });
    return response.data;
  },

  // 自动生成报告并保存
  autoGenerateAndSave: async (
    reportType: 'daily' | 'weekly' | 'biweekly' | 'monthly',
    referenceDate?: string,
    projectIds?: number[]
  ): Promise<{ success: boolean; id: number; message: string }> => {
    const params: any = { report_type: reportType };
    if (referenceDate) params.reference_date = referenceDate;
    if (projectIds && projectIds.length > 0) params.project_ids = projectIds.join(',');
    const response = await api.post('/reports/auto/generate-and-save', null, { params });
    return response.data;
  },

  // 下载报告 Word 文档（返回下载 URL）
  getDownloadUrl: (
    reportType: 'daily' | 'weekly' | 'biweekly' | 'monthly',
    referenceDate?: string,
    projectIds?: number[]
  ): string => {
    const params = new URLSearchParams({ report_type: reportType });
    if (referenceDate) params.append('reference_date', referenceDate);
    if (projectIds && projectIds.length > 0) params.append('project_ids', projectIds.join(','));
    return `/api/reports/auto/download?${params.toString()}`;
  },

  // ============ 报告模板相关 API ============
  // 获取模板列表
  getTemplates: async (params?: { type?: string }): Promise<ReportTemplate[]> => {
    const response = await api.get('/reports/templates', { params });
    return response.data as ReportTemplate[];
  },

  // 创建模板
  createTemplate: async (data: {
    name: string;
    type: 'daily' | 'weekly' | 'biweekly' | 'monthly';
    content: string;
    description?: string;
  }): Promise<ReportTemplate> => {
    const response = await api.post('/reports/templates', data);
    return response.data as ReportTemplate;
  },

  // 更新模板
  updateTemplate: async (id: string, data: Partial<ReportTemplate>): Promise<ReportTemplate> => {
    const response = await api.put(`/reports/templates/${id}`, data);
    return response.data as ReportTemplate;
  },

  // 删除模板
  deleteTemplate: async (id: string): Promise<void> => {
    await api.delete(`/reports/templates/${id}`);
  },
};

// 消息服务
export const messageApi = {
  // 获取消息列表
  getMessages: async (params?: {
    page?: number;
    pageSize?: number;
    read?: boolean;
  }): Promise<Message[]> => {
    const response = await api.get('/messages', { params });
    return response.data as Message[];
  },

  // 标记消息为已读
  markAsRead: async (id: string): Promise<void> => {
    await api.put(`/messages/${id}/read`);
  },

  // 标记所有消息为已读
  markAllAsRead: async (): Promise<void> => {
    await api.put('/messages/read-all');
  },
};

// 文件服务
export const fileApi = {
  // 上传文件
  uploadFile: async (
    file: File
  ): Promise<{
    id: string;
    url: string;
    name: string;
    size: number;
    type: string;
  }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data as {
      id: string;
      url: string;
      name: string;
      size: number;
      type: string;
    };
  },

  // 删除文件
  deleteFile: async (id: string): Promise<void> => {
    await api.delete(`/files/${id}`);
  },
};

// 导出服务
export const exportApi = {
  // 导出 Excel
  exportExcel: async (
    type: 'projects' | 'tasks' | 'issues' | 'users',
    params?: any
  ): Promise<Blob> => {
    const response = await api.get(`/export/excel/${type}`, {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // 导出 PDF
  exportPdf: async (
    type: 'projects' | 'tasks' | 'issues' | 'users',
    params?: any
  ): Promise<Blob> => {
    const response = await api.get(`/export/pdf/${type}`, {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};

// 通知服务
export const notificationApi = {
  // 获取通知列表
  getNotifications: async (params?: {
    page?: number;
    pageSize?: number;
    read?: boolean;
    type?: string;
  }): Promise<Notification[]> => {
    const response = await api.get('/notifications', { params });
    return response.data as Notification[];
  },

  // 获取通知详情
  getNotification: async (id: string): Promise<Notification> => {
    const response = await api.get(`/notifications/${id}`);
    return response.data as Notification;
  },

  // 标记为已读
  markAsRead: async (id: string): Promise<void> => {
    await api.put(`/notifications/${id}/read`);
  },

  // 标记所有为已读
  markAllAsRead: async (): Promise<void> => {
    await api.put('/notifications/read-all');
  },

  // 删除通知
  deleteNotification: async (id: string): Promise<void> => {
    await api.delete(`/notifications/${id}`);
  },

  // 发送通知
  sendNotification: async (data: {
    type: 'notification' | 'chat' | 'system' | 'task' | 'project';
    title: string;
    content: string;
    recipientId?: string;
    projectId?: string;
    taskId?: string;
  }): Promise<Notification> => {
    const response = await api.post('/notifications', data);
    return response.data as Notification;
  },
};

// 待办服务
export const todoApi = {
  // 获取待办列表
  getTodos: async (params?: { completed?: boolean }): Promise<Todo[]> => {
    const response = await api.get('/todos', { params });
    return response.data as Todo[];
  },

  // 创建待办
  createTodo: async (data: {
    title: string;
    priority: 'low' | 'medium' | 'high';
    dueDate?: string;
    tags?: string[];
  }): Promise<Todo> => {
    const response = await api.post('/todos', data);
    return response.data as Todo;
  },

  // 更新待办
  updateTodo: async (id: string, data: Partial<Todo>): Promise<Todo> => {
    const response = await api.put(`/todos/${id}`, data);
    return response.data as Todo;
  },

  // 删除待办
  deleteTodo: async (id: string): Promise<void> => {
    await api.delete(`/todos/${id}`);
  },

  // Todo 转为 Task
  convertToTask: async (id: string): Promise<{ task_id: string; message: string }> => {
    const response = await api.post(`/todos/${id}/convert-to-task`);
    return response.data;
  },

  // 获取我的待办（子任务和 Issue）
  getMyTodos: async (): Promise<{ subtasks: MySubtask[]; issues: Issue[] }> => {
    const response = await api.get('/todos/my');
    const data = response.data;
    return {
      subtasks: data.subtasks || [],
      issues: (data.issues || []).map((issue: any) => convertIssueToFrontend(issue)),
    } as { subtasks: MySubtask[]; issues: Issue[] };
  },
};

// Scrum看板服务
export const scrumApi = {
  // 获取Scrum看板数据
  getBoard: async (_projectId: string): Promise<{ data: any[] }> => {
    console.warn('scrumApi.getBoard: 后端未实现，返回空数据');
    return { data: [] };
  },

  // 创建史诗
  createEpic: async (_data: {
    title: string;
    description?: string;
    projectId: string;
    color: string;
  }): Promise<any> => {
    console.warn('scrumApi.createEpic: 后端未实现');
    return {};
  },

  // 创建用户故事
  createStory: async (_data: {
    title: string;
    description?: string;
    epicId: string;
    storyPoints?: number;
    priority?: string;
  }): Promise<any> => {
    console.warn('scrumApi.createStory: 后端未实现');
    return {};
  },

  // 获取史诗列表
  getEpics: async (_projectId: string): Promise<any[]> => {
    console.warn('scrumApi.getEpics: 后端未实现，返回空数组');
    return [];
  },

  // 获取用户故事列表
  getStories: async (_epicId: string): Promise<any[]> => {
    console.warn('scrumApi.getStories: 后端未实现，返回空数组');
    return [];
  },
};

// 资源服务
export const resourceApi = {
  // 获取资源列表
  getResources: async (params?: {
    project_id?: number;
  }): Promise<Resource[]> => {
    const response = await api.get('/resources', { params });
    return response.data as Resource[];
  },

  // 创建资源
  createResource: async (data: {
    name: string;
    description?: string;
    resource_type: 'human' | 'software' | 'hardware' | 'external' | 'file';
    user_id?: number;
    project_id?: number;
  }): Promise<Resource> => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    formData.append('resource_type', data.resource_type);
    if (data.user_id) formData.append('user_id', String(data.user_id));
    if (data.project_id) formData.append('project_id', String(data.project_id));

    const response = await api.post('/resources', formData);
    return response.data as Resource;
  },

  // 更新资源
  updateResource: async (id: string, data: {
    name?: string;
    description?: string;
    resource_type?: string;
    user_id?: number;
    project_id?: number;
  }): Promise<Resource> => {
    const response = await api.put(`/resources/${id}`, data);
    return response.data as Resource;
  },

  // 删除资源
  deleteResource: async (id: string): Promise<void> => {
    await api.delete(`/resources/${id}`);
  },
};
